// Admin › OTA Releases › Register updates: each merge's drafted changes to the
// help register, under the app's coming or released version (vortex-api
// routes/adminRegisterUpdates.ts).
// docs/core/operations/REGISTER_UPDATES.md

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../../services/api';

export type ChangeStatus = 'open' | 'accepted' | 'rejected' | 'applied' | 'conflict';
export type ChangeKind = 'update' | 'retire' | 'new';

export interface RegisterRelease {
  id: string;
  /** Null while the version is still coming. */
  tag: string | null;
  /** The release-please component, e.g. vortex-launcher. */
  component: string;
  released: boolean;
  /** The register's Module / App: Home, VortexNav, ... */
  module: string;
  /** The app's icon key (launcher, vn, va, vt, th, wv). */
  app: string;
  version: string;
  tagged_at: string | null;
  release_url: string | null;
  counts?: Record<ChangeStatus, number>;
  total?: number;
}

/** One pull request merged to main, drafted for this version. */
export interface RegisterMerge {
  id: string;
  key: string;
  pr_number: number | null;
  /** The PR's title, as merged ("feat(launcher): Volume pop-out ..."). */
  title: string;
  author: string;
  url: string | null;
  merged_at: string | null;
  shas: string[];
  position: number;
}

export interface RegisterChange {
  id: string;
  release_id: string;
  /** The merge whose draft this is; null for a whole-release draft. */
  merge_id: string | null;
  /** Changes that only re-route paths the same way share a bundle, checked together. */
  bundle_key?: string | null;
  bundle_title?: string | null;
  ref: string;
  position: number;
  kind: ChangeKind;
  doc_id: string | null;
  anchor_doc_id: string | null;
  sheet_tab: string;
  /** Where it is on the Scout ("Menu › My Boat"). */
  screen: string;
  element: string;
  /** The row's fact columns when drafted, by heading. */
  current: Record<string, string>;
  changes: { column: string; proposed: string; reason: string }[];
  check_steps: string[];
  look_for: string[];
  commits: { sha: string; author: string; title: string; pr?: number | null }[];
  status: ChangeStatus;
  decision: { columns: Record<string, string>; note: string | null } | null;
  decided_by_name: string | null;
  decided_at: string | null;
  /** The row in the sheet now, when it changed there after the draft. */
  sheet_now: Record<string, string> | null;
  applied_at: string | null;
}

export interface RegisterReleaseDetail {
  release: RegisterRelease;
  /** In merge order; empty for a whole-release draft. */
  merges: RegisterMerge[];
  changes: RegisterChange[];
}

export type RegisterDecision =
  | { action: 'accept'; columns: Record<string, string>; note?: string | null }
  | { action: 'reject'; note?: string | null }
  | { action: 'reopen' };

/** The register's fact columns, in sheet order. */
export const FACT_COLUMNS = [
  'Element', 'Item Type', 'Primary Interaction', 'Navigation Path',
  'What it does', 'How it is used', 'Requirements / Limitations', 'Options / Defaults / Units',
] as const;

/** Still needs someone: not yet decided, or the sheet moved on since. */
export const needsCheck = (c: RegisterChange) => c.status === 'open' || c.status === 'conflict';
/** Decided, whether or not the job has written it yet. */
export const isDone = (c: RegisterChange) => c.status === 'accepted' || c.status === 'rejected' || c.status === 'applied';

export function useRegisterReleases() {
  return useQuery<RegisterRelease[]>({
    queryKey: ['register', 'releases'],
    queryFn: () => api.adminRegisterReleases(),
    staleTime: 30_000,
  });
}

export function useRegisterRelease(id: string | undefined) {
  return useQuery<RegisterReleaseDetail>({
    queryKey: ['register', 'release', id],
    queryFn: () => api.adminRegisterRelease(id!),
    enabled: !!id,
    staleTime: 30_000,
  });
}

/** Records a decision and puts the returned change straight into the cached release. */
export function useDecide(releaseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<RegisterChange, Error, { id: string; body: RegisterDecision }>({
    mutationFn: ({ id, body }) => api.adminRegisterDecide(id, body),
    onSuccess: (change) => {
      qc.setQueryData<RegisterReleaseDetail>(['register', 'release', releaseId], (old) =>
        old ? { ...old, changes: old.changes.map((c) => (c.id === change.id ? change : c)) } : old);
      qc.invalidateQueries({ queryKey: ['register', 'releases'] });
    },
  });
}

/** "feat(launcher): setup guide in the frame" → "Setup guide in the frame". */
export const plainTitle = (title: string) => {
  const t = title.replace(/^\w+(\([^)]*\))?!?:\s*/, '');
  return t.charAt(0).toUpperCase() + t.slice(1);
};

/** The other pull requests behind a change, besides its own merge's. */
export const otherPrs = (c: RegisterChange, merge: RegisterMerge | undefined) =>
  [...new Set(c.commits.map((x) => x.pr).filter((n): n is number => !!n && n !== merge?.pr_number))];

/** Decides a bundle in one call, putting the returned changes straight into the cached release. */
export function useDecideMany(releaseId: string | undefined) {
  const qc = useQueryClient();
  return useMutation<{ changes: RegisterChange[]; failed: { id: string; error: string }[] }, Error, { ids: string[]; action: 'accept' | 'reject'; note?: string | null }>({
    mutationFn: (body) => api.adminRegisterDecideMany(body),
    onSuccess: ({ changes }) => {
      const byId = new Map(changes.map((c) => [c.id, c]));
      qc.setQueryData<RegisterReleaseDetail>(['register', 'release', releaseId], (old) =>
        old ? { ...old, changes: old.changes.map((c) => byId.get(c.id) ?? c) } : old);
      qc.invalidateQueries({ queryKey: ['register', 'releases'] });
    },
  });
}

// ---- Track changes ----

export type DiffPart = { text: string; kind: 'same' | 'removed' | 'added' };

/**
 * Word-level changes from `before` to `after`. A Navigation Path compares by
 * step ("Home → Menu → ..."). Returns null when little of the text survives,
 * so a rewrite shows as old over new rather than a tangle of fragments.
 * (Under 40% of the letters kept, for prose.)
 */
export function trackChanges(before: string, after: string, byStep = false): DiffPart[] | null {
  const split = byStep ? /( → )/ : /(\s+)/;
  const a = before.split(split).filter(Boolean);
  const b = after.split(split).filter(Boolean);
  const n = a.length, m = b.length;
  const lcs: number[][] = Array.from({ length: n + 1 }, () => new Array<number>(m + 1).fill(0));
  for (let i = n - 1; i >= 0; i--) {
    for (let j = m - 1; j >= 0; j--) lcs[i][j] = a[i] === b[j] ? lcs[i + 1][j + 1] + 1 : Math.max(lcs[i + 1][j], lcs[i][j + 1]);
  }
  const raw: DiffPart[] = [];
  let i = 0, j = 0;
  while (i < n && j < m) {
    if (a[i] === b[j]) { raw.push({ text: a[i], kind: 'same' }); i++; j++; }
    else if (lcs[i + 1][j] >= lcs[i][j + 1]) raw.push({ text: a[i++], kind: 'removed' });
    else raw.push({ text: b[j++], kind: 'added' });
  }
  while (i < n) raw.push({ text: a[i++], kind: 'removed' });
  while (j < m) raw.push({ text: b[j++], kind: 'added' });

  // A path always compares by step; prose that keeps under half its words is a rewrite.
  const letters = (t: string) => t.replace(/\s+/g, '').length;
  const kept = raw.filter((p) => p.kind === 'same').reduce((t, p) => t + letters(p.text), 0);
  if (!byStep && kept < 0.4 * Math.max(letters(before), letters(after))) return null;

  // A space or arrow between two changed runs joins them, so a changed phrase reads as one.
  const gap = byStep ? /^( → |\s+)$/ : /^\s+$/;
  const out: DiffPart[] = [];
  raw.forEach((p, idx) => {
    let kind = p.kind;
    if (kind === 'same' && gap.test(p.text)) {
      const prev = out[out.length - 1];
      const next = raw.slice(idx + 1).find((q) => q.kind !== 'same' || !gap.test(q.text));
      if (prev && next && prev.kind !== 'same' && next.kind !== 'same') kind = next.kind;
    }
    const last = out[out.length - 1];
    if (last && last.kind === kind) last.text += p.text;
    else out.push({ text: p.text, kind });
  });
  return out;
}

// ---- Dates ----

const NZ = 'Pacific/Auckland';
/** "23 Oct" in New Zealand. */
export const nzDay = (iso: string | null) =>
  iso ? new Intl.DateTimeFormat('en-NZ', { timeZone: NZ, day: 'numeric', month: 'short' }).format(new Date(iso)) : '';
